/**
 * Электронный чек ОФД: разбор напечатанного текста.
 *
 * Чек из приложения банка или магазина приходит PDF-файлом, в котором лежит
 * **настоящий текст**, а не картинка. Прежде мы этого не замечали: страницы
 * рисовались в JPEG и отправлялись распознаванию, как снимок с кассы. Так
 * точные числа сначала превращались в пиксели, потом обратно в числа — с
 * потерями, за деньги и с задержкой. В живом файле «ПЕТЕЛ.Куриное филе на
 * подл1кг» распознавание прочло как «подлікг», а половина чека не дошла вовсе.
 *
 * Здесь чек читается так, как он напечатан. Числа не угадываются моделью и не
 * восстанавливаются из картинки — они берутся из документа, а правильность
 * проверяется его же арифметикой:
 *
 * · в каждой позиции `цена × количество = сумма`;
 * · `Σ позиций = ИТОГ`.
 *
 * Не сошлось — состав не показывается вовсе. Показать половину чека хуже, чем
 * не показать ничего: человек решит, что видит покупку целиком.
 *
 * Разбирается форма ФФД: «N. Название», следом «Цена * Кол ц * к ед = сумма».
 * Незнакомую вёрстку модуль не додумывает, а честно отдаёт назад — и документ
 * уходит обычным путём, через распознавание.
 */
import { pd, type PlainDate } from './date.js';

/** Позиция чека. Рубли, как напечатано, — в копейки переводит вызывающий. */
export interface FiscalItem {
  name: string;
  qty: number;
  price: number;
  sum: number;
}

/** Что это за документ. Возврат расходом не является. */
export type FiscalKind = 'sale' | 'refund' | 'unknown';

export interface FiscalReceipt {
  /** Состав можно показывать только при true: арифметика сошлась целиком. */
  trusted: boolean;
  kind: FiscalKind;
  merchant: string | null;
  date: PlainDate | null;
  time: string | null;
  total: number | null;
  /**
   * Итог посчитан по позициям, потому что в документе его нет.
   *
   * Так устроены многие документы, которые человек и правда загружает: состав
   * заказа в приложении магазина, счёт из кафе. Отказывать им нельзя, но и
   * молчать нельзя: пропущенную строку в таком документе поймать нечем — итог
   * и есть тот свидетель, которого здесь недостаёт.
   */
  totalFromItems: boolean;
  items: FiscalItem[];
  /** Позиции по нулевой цене: покупкой они не были. */
  free: { name: string; qty: number | null }[];
  /**
   * Фискальные реквизиты: ФН, ФД и ФПД.
   *
   * Самый надёжный ключ повтора: один и тот же чек, сохранённый дважды разными
   * файлами, даёт разные хеши и одни и те же реквизиты.
   */
  fiscal: { fn?: string; fd?: string; fp?: string };
  problems: string[];
}

/** Допуск в рублях: весовой товар округляется на копейки. */
const TOLERANCE = 1;

const EMPTY: FiscalReceipt = {
  trusted: false, kind: 'unknown', merchant: null, date: null, time: null,
  total: null, totalFromItems: false, items: [], free: [], fiscal: {}, problems: [],
};

/** Число из чека: «1 078,17» и «1078.17» — одно и то же. */
function num(raw: string): number | null {
  const clean = raw.replace(/[\s  ]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,3})?$/.test(clean)) return null;
  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
}

/**
 * Двойная печать жирного текста.
 *
 * Название в чеке нарисовано дважды со сдвигом в доли пункта — так PDF
 * изображает жирность. Клиент убирает повтор по координатам, но текст может
 * прийти и из другого источника, поэтому очевидное удвоение снимается здесь
 * ещё раз: «Бананы Бананы» — это один товар, а не два.
 */
function undouble(s: string): string {
  const t = s.trim();
  const half = (t.length - 1) / 2;
  if (Number.isInteger(half) && t[half] === ' ' && t.slice(0, half) === t.slice(half + 1)) {
    return t.slice(0, half);
  }
  return t;
}

/** Строка вида «Цена * Кол 84.99 * 1 шт. = 84.99». */
const VALUE = /Цена\s*[*х×x]\s*Кол\s+([\d\s.,  ]+?)\s*[*х×x]\s*([\d\s.,  ]+?)\s*([^\d=]*?)\s*=\s*([\d\s.,  ]+)\s*$/i;
/** Строка вида «1. Название товара». */
const NAME = /^(\d{1,3})\s*\.\s*(.+)$/;
/**
 * Итог чека.
 *
 * Подпись у него разная: кассовый чек пишет «ИТОГ», приложение банка — «Total»,
 * счёт — «К оплате» или «Всего». «Подытог» исключён намеренно: он считается по
 * ценам до скидки и с суммой позиций не сходится.
 */
const TOTAL = /^(?:ИТОГО?|TOTAL|ВСЕГО|К\s+ОПЛАТЕ|СУММА\s+К\s+ОПЛАТЕ)\s*:?\s+(\d[\d\s.,\u00a0\u202f]*)\s*(?:₽|руб\.?|Р)?$/i;

/**
 * Чек из напечатанных строк.
 *
 * `lines` — строки документа сверху вниз, по одной на строку бумаги. Порядок
 * важен: название и числа позиции стоят на соседних строках, и связывает их
 * именно соседство.
 */
export function parseFiscalText(lines: readonly string[]): FiscalReceipt {
  const text = lines.map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);

  // Признак формы: без него незнакомый документ разбирался бы наугад.
  const looksFiscal = text.some((l) => VALUE.test(l))
    && text.some((l) => /КАССОВЫЙ\s+ЧЕК|ИТОГ|TOTAL|К\s+ОПЛАТЕ/i.test(l));
  if (!looksFiscal) return { ...EMPTY, problems: ['Это не похоже на электронный чек'] };

  const items: FiscalItem[] = [];
  const free: { name: string; qty: number | null }[] = [];
  const problems: string[] = [];
  let pending: string | null = null;

  for (const line of text) {
    const value = VALUE.exec(line);
    if (!value) {
      const named = NAME.exec(line);
      // Название держим до ближайшей строки с числами: между ними в чеке
      // ничего не печатают.
      if (named) pending = undouble(named[2] ?? '');
      else if (!/^(НДС|Способ расчета|Предмет расчета|Акциз|Код товара)/i.test(line)) pending = null;
      continue;
    }

    const price = num(value[1] ?? '');
    const qty = num(value[2] ?? '');
    const sum = num(value[4] ?? '');
    const name = pending;
    pending = null;
    if (price === null || qty === null || sum === null || !name) {
      problems.push('Позицию не удалось прочитать целиком');
      continue;
    }

    // Позиция по нулевой цене — не покупка: так печатают подарок и предмет,
    // выданный к акции. В сумму чека она не входит и тратой не была.
    if (sum === 0) { free.push({ name, qty }); continue; }

    if (Math.abs(price * qty - sum) > TOLERANCE) {
      problems.push(`«${name}»: цена и количество не сходятся с суммой`);
    }
    items.push({ name, qty, price, sum });
  }

  const printed = pick(text, TOTAL);
  const sumOfItems = items.reduce((a, i) => a + i.sum, 0);
  if (printed !== null && Math.abs(sumOfItems - printed) > TOLERANCE) {
    problems.push('Сумма позиций не сошлась с итогом чека');
  }
  if (items.length === 0) problems.push('В документе не нашлось ни одной позиции');

  // Итога в документе нет, а позиции прочитаны и сошлись каждая сама с собой.
  // Складываем их: сумма покупки — это и есть сумма купленного. Признак уходит
  // наверх, и человек видит, что число посчитано, а не прочитано.
  const totalFromItems = printed === null && items.length > 0 && problems.length === 0;
  const total = printed ?? (totalFromItems ? sumOfItems : null);
  if (total === null) problems.push('В документе не нашёлся итог');

  const kind: FiscalKind = text.some((l) => /возврат\s+прихода/i.test(l))
    ? 'refund'
    : text.some((l) => /^приход$/i.test(l) || /КАССОВЫЙ\s+ЧЕК/i.test(l))
      ? 'sale'
      : 'unknown';

  return {
    trusted: problems.length === 0,
    kind,
    merchant: merchantOf(text),
    date: dateOf(text),
    time: timeOf(text),
    total,
    totalFromItems,
    items,
    free,
    fiscal: fiscalOf(text),
    problems,
  };
}

/** Первое число по образцу. */
function pick(text: readonly string[], re: RegExp): number | null {
  for (const line of text) {
    const m = re.exec(line);
    if (m) {
      const n = num(m[1] ?? '');
      if (n !== null) return n;
    }
  }
  return null;
}

/**
 * Имя, под которым человек узнаёт магазин.
 *
 * Юридическое лицо в чеке — «ООО "Агроторг"», а человек был в «Пятёрочке».
 * Название торговой точки печатается в «Месте расчётов» после номера, и
 * только оно совпадает с тем, что человек помнит о покупке.
 */
function merchantOf(text: readonly string[]): string | null {
  const place = text.find((l) => /^Место\s+расчет/i.test(l));
  if (place) {
    const tail = place.split('-').pop()?.trim();
    if (tail && /[А-Яа-яЁёA-Za-z]{3}/.test(tail) && tail.length <= 60) return tail;
  }
  const quoted = text.find((l) => /"[^"]{2,60}"/.test(l));
  const inQuotes = quoted ? /"([^"]{2,60})"/.exec(quoted)?.[1] : null;
  if (inQuotes) return inQuotes;
  return text[0] ?? null;
}

function dateOf(text: readonly string[]): PlainDate | null {
  const line = text.find((l) => /Дата/i.test(l) && /\d{2}\.\d{2}\.\d{4}/.test(l))
    ?? text.find((l) => /\d{2}\.\d{2}\.\d{4}/.test(l));
  const m = line ? /(\d{2})\.(\d{2})\.(\d{4})/.exec(line) : null;
  if (!m) return null;
  try {
    return pd(`${m[3]}-${m[2]}-${m[1]}`);
  } catch {
    return null;
  }
}

function timeOf(text: readonly string[]): string | null {
  const line = text.find((l) => /Дата|Время/i.test(l) && /\d{1,2}:\d{2}/.test(l));
  const m = line ? /(\d{1,2}):(\d{2})/.exec(line) : null;
  return m ? `${(m[1] ?? '').padStart(2, '0')}:${m[2]}` : null;
}

/** ФН, ФД и ФПД: по ним один чек узнаётся в любом файле. */
function fiscalOf(text: readonly string[]): { fn?: string; fd?: string; fp?: string } {
  const take = (re: RegExp): string | undefined => {
    for (const line of text) {
      const m = re.exec(line);
      if (m?.[1]) return m[1];
    }
    return undefined;
  };
  const fn = take(/^ФН\s+(\d{6,20})$/i);
  const fd = take(/^ФД\s+(\d{1,20})$/i);
  const fp = take(/^ФПД?\s+(\d{1,20})$/i);
  return {
    ...(fn ? { fn } : {}),
    ...(fd ? { fd } : {}),
    ...(fp ? { fp } : {}),
  };
}
